/**
 * DayOne App Framework — client-side fallback scaffold for builder projects.
 * Framework-only files: entry points, lib helpers, UI components, auth context.
 * App-specific files (App.tsx, Shell.tsx, pages, index.css) come from the AI.
 * Must match the edge function scaffold (generate-app-scaffold) exactly.
 * Inject via getDayOneAppFrameworkScaffold() when creating or resetting a project.
 * Do not modify the DayOne host app; this is for generated builder projects only.
 */

export interface ScaffoldFile {
  path: string;
  content: string;
}

export function getDayOneAppFrameworkScaffold(appName: string = "App"): ScaffoldFile[] {
  return [
    // --- Entry & config ---
    {
      path: "index.html",
      content: `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>${appName}</title>
    <script src="https://cdn.tailwindcss.com"></script>
  </head>
  <body>
    <div id="root"></div>
    <script type="module" src="/src/main.tsx"></script>
  </body>
</html>
`,
    },
    {
      path: "src/vite-env.d.ts",
      content: `/// <reference types="vite/client" />
`,
    },
    {
      path: "src/main.tsx",
      content: `import React from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App";

createRoot(document.getElementById("root")!).render(<App />);
`,
    },

    // --- Loading placeholders (replaced by AI on first build) ---
    {
      path: "src/index.css",
      content: `@tailwind base;
@tailwind components;
@tailwind utilities;

:root {
  --primary: 59 130 246;
  --primary-foreground: 255 255 255;
  --background: 255 255 255;
  --foreground: 15 23 42;
  --card: 255 255 255;
  --card-foreground: 15 23 42;
  --muted: 248 250 252;
  --muted-foreground: 100 116 139;
  --border: 226 232 240;
  --destructive: 239 68 68;
  --success: 34 197 94;
  --warning: 245 158 11;
  --sidebar: 248 250 252;
  --sidebar-foreground: 71 85 105;
  --sidebar-border: 226 232 240;
  --sidebar-active: var(--primary);
  --sidebar-active-foreground: var(--primary-foreground);
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

body {
  margin: 0;
  min-height: 100vh;
  background: rgb(var(--background));
  color: rgb(var(--foreground));
  font-family: system-ui, -apple-system, sans-serif;
}
`,
    },
    {
      path: "src/App.tsx",
      content: `import React from "react";

export default function App() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[rgb(var(--background))]">
      <div className="text-center">
        <svg className="animate-spin h-8 w-8 mx-auto mb-4 text-[rgb(var(--primary))]" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <p className="text-[rgb(var(--muted-foreground))] text-sm">Building your app...</p>
      </div>
    </div>
  );
}
`,
    },

    // --- Lib helpers ---
    {
      path: "src/lib/cn.ts",
      content: `export { cn } from "./utils";
`,
    },
    {
      path: "src/lib/utils.ts",
      content: `type ClassValue = string | number | boolean | undefined | null | ClassValue[] | Record<string, boolean>;

export function cn(...inputs: ClassValue[]): string {
  const list: string[] = [];
  for (const x of inputs) {
    if (x == null || typeof x === "boolean") continue;
    if (typeof x === "string" && x) list.push(x);
    else if (typeof x === "number") list.push(String(x));
    else if (Array.isArray(x)) list.push(cn(...x));
    else if (typeof x === "object") {
      for (const [k, v] of Object.entries(x)) if (v) list.push(k);
    }
  }
  return list.join(" ");
}

// Format a date to a human-readable string
export function formatDate(date: Date | string | null | undefined, options?: Intl.DateTimeFormatOptions): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", options ?? { month: "short", day: "numeric", year: "numeric" });
}

// Format a date with time
export function formatDateTime(date: Date | string | null | undefined): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

// Check if a date is overdue (past current time)
export function isOverdue(date: Date | string | null | undefined): boolean {
  if (!date) return false;
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return false;
  return d.getTime() < Date.now();
}

// Check if a date is today
export function isToday(date: Date | string | null | undefined): boolean {
  if (!date) return false;
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return false;
  const today = new Date();
  return d.toDateString() === today.toDateString();
}

// Format currency with symbol
export function formatCurrency(amount: number | null | undefined, currency: string = "USD"): string {
  if (amount == null || isNaN(amount)) return "$0.00";
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount);
}

// Truncate text with ellipsis
export function truncateText(text: string | null | undefined, maxLength: number): string {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + "...";
}

// Get relative time (e.g., "2 days ago", "in 3 hours")
export function getRelativeTime(date: Date | string | null | undefined): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "";
  const now = Date.now();
  const diff = d.getTime() - now;
  const absDiff = Math.abs(diff);
  const minutes = Math.floor(absDiff / 60000);
  const hours = Math.floor(absDiff / 3600000);
  const days = Math.floor(absDiff / 86400000);
  const isPast = diff < 0;

  if (minutes < 1) return "just now";
  if (minutes < 60) return isPast ? \\\`\\\${minutes}m ago\\\` : \\\`in \\\${minutes}m\\\`;
  if (hours < 24) return isPast ? \\\`\\\${hours}h ago\\\` : \\\`in \\\${hours}h\\\`;
  if (days < 7) return isPast ? \\\`\\\${days}d ago\\\` : \\\`in \\\${days}d\\\`;
  return formatDate(d);
}

// Generate initials from a name
export function getInitials(name: string | null | undefined): string {
  if (!name) return "";
  return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
}

// Capitalize first letter of each word
export function capitalizeWords(text: string | null | undefined): string {
  if (!text) return "";
  return text.replace(/\\\\b\\\\w/g, c => c.toUpperCase());
}

// ═══════════════════════════════════════════════════════════════════════════
// NUMBER FORMATTING
// ═══════════════════════════════════════════════════════════════════════════

// Format a number with locale-aware separators
export function formatNumber(n: number | null | undefined): string {
  if (n == null || isNaN(n)) return "0";
  return new Intl.NumberFormat("en-US").format(n);
}

// Format a number as percentage
export function formatPercent(n: number | null | undefined, decimals: number = 0): string {
  if (n == null || isNaN(n)) return "0%";
  return (n * 100).toFixed(decimals) + "%";
}

// ═══════════════════════════════════════════════════════════════════════════
// TIME FORMATTING
// ═══════════════════════════════════════════════════════════════════════════

// Format time only (no date)
export function formatTime(date: Date | string | null | undefined): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
}

// Get human-readable time until a date
export function getTimeUntil(date: Date | string | null | undefined): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "";
  const ms = d.getTime() - Date.now();
  if (ms < 0) return "past";
  const minutes = Math.floor(ms / 60000);
  if (minutes < 60) return minutes + "m";
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours + "h";
  return Math.floor(hours / 24) + "d";
}

// Get minutes until a date (numeric)
export function getMinutesUntil(date: Date | string | null | undefined): number {
  if (!date) return 0;
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return 0;
  return Math.floor((d.getTime() - Date.now()) / 60000);
}

// ═══════════════════════════════════════════════════════════════════════════
// ID & ASYNC
// ═══════════════════════════════════════════════════════════════════════════

// Generate a random ID (NOT crypto-secure, use for UI keys only)
export function generateId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// Delay execution
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Debounce a function
export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// STRING UTILITIES
// ═══════════════════════════════════════════════════════════════════════════

// Convert string to URL-friendly slug
export function slugify(text: string | null | undefined): string {
  if (!text) return "";
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

// Truncate string (simple version)
export function truncate(text: string, maxLength: number): string {
  return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
}

// Capitalize first letter only
export function capitalize(text: string | null | undefined): string {
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// ═══════════════════════════════════════════════════════════════════════════
// MATH UTILITIES
// ═══════════════════════════════════════════════════════════════════════════

// Clamp a number between min and max
export function clamp(n: number, min: number, max: number): number {
  return Math.min(Math.max(n, min), max);
}

// ═══════════════════════════════════════════════════════════════════════════
// ARRAY UTILITIES
// ═══════════════════════════════════════════════════════════════════════════

// Get unique values from an array
export function unique<T>(arr: T[]): T[] {
  return Array.from(new Set(arr));
}

// Group array items by a key function
export function groupBy<T, K extends string | number>(
  arr: T[],
  key: (item: T) => K
): Record<K, T[]> {
  return arr.reduce((acc, item) => {
    const k = key(item);
    (acc[k] = acc[k] || []).push(item);
    return acc;
  }, {} as Record<K, T[]>);
}

// Sort array by a key function
export function sortBy<T>(arr: T[], key: (item: T) => string | number | Date): T[] {
  return [...arr].sort((a, b) => {
    const av = key(a);
    const bv = key(b);
    return av < bv ? -1 : av > bv ? 1 : 0;
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// VALIDATION & PARSING
// ═══════════════════════════════════════════════════════════════════════════

// Validate email format
export function isValidEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  return /^[^\\\\s@]+@[^\\\\s@]+\\\\.[^\\\\s@]+$/.test(email);
}

// Safely parse JSON with fallback
export function parseJSON<T>(text: string, fallback: T): T {
  try {
    return JSON.parse(text);
  } catch {
    return fallback;
  }
}
`,
    },

    // --- Auth scaffold (Supabase) ---
    {
      path: "src/lib/supabase.ts",
      content: `import { createClient, SupabaseClient } from "@supabase/supabase-js";

// In preview mode these will be empty strings — stub client is used (auth becomes no-op).
// Set NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY in your .env for real auth.
const url = (typeof process !== "undefined" && process.env?.NEXT_PUBLIC_SUPABASE_URL) || (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_SUPABASE_URL) || "";
const key = (typeof process !== "undefined" && process.env?.NEXT_PUBLIC_SUPABASE_ANON_KEY) || (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_SUPABASE_ANON_KEY) || "";

// Preview-mode stub: provides same interface as real client but returns safe no-ops.
// This ensures supabase.auth.X never crashes, even when credentials are missing.
const stubAuth = {
  getSession: async () => ({ data: { session: null }, error: null }),
  getUser: async () => ({ data: { user: null }, error: null }),
  onAuthStateChange: (_cb: any) => ({ data: { subscription: { unsubscribe: () => {} } } }),
  signInWithPassword: async () => ({ data: { user: null, session: null }, error: { message: "Auth disabled in preview mode" } }),
  signUp: async () => ({ data: { user: null, session: null }, error: { message: "Auth disabled in preview mode" } }),
  signOut: async () => ({ error: null }),
  resetPasswordForEmail: async () => ({ data: null, error: { message: "Auth disabled in preview mode" } }),
  updateUser: async () => ({ data: { user: null }, error: { message: "Auth disabled in preview mode" } }),
};

const stubQuery = {
  select: function() { return this; },
  eq: function() { return this; },
  neq: function() { return this; },
  gt: function() { return this; },
  lt: function() { return this; },
  gte: function() { return this; },
  lte: function() { return this; },
  like: function() { return this; },
  ilike: function() { return this; },
  is: function() { return this; },
  in: function() { return this; },
  order: function() { return this; },
  limit: function() { return this; },
  single: function() { return Promise.resolve({ data: null, error: null }); },
  maybeSingle: function() { return Promise.resolve({ data: null, error: null }); },
  then: function(resolve: any) { return resolve({ data: [], error: null }); },
};

const stubClient = {
  auth: stubAuth,
  from: () => ({
    select: () => stubQuery,
    insert: () => Promise.resolve({ data: null, error: { message: "Database disabled in preview mode" } }),
    update: () => ({ eq: () => Promise.resolve({ data: null, error: { message: "Database disabled in preview mode" } }) }),
    delete: () => ({ eq: () => Promise.resolve({ data: null, error: { message: "Database disabled in preview mode" } }) }),
    upsert: () => Promise.resolve({ data: null, error: { message: "Database disabled in preview mode" } }),
  }),
  storage: {
    from: () => ({
      upload: async () => ({ data: null, error: { message: "Storage disabled in preview mode" } }),
      download: async () => ({ data: null, error: { message: "Storage disabled in preview mode" } }),
      getPublicUrl: () => ({ data: { publicUrl: "" } }),
    }),
  },
  rpc: async () => ({ data: null, error: { message: "RPC disabled in preview mode" } }),
};

export const isPreviewMode = !url || !key;
export const supabase: SupabaseClient | typeof stubClient = url && key ? createClient(url, key) : stubClient;
`,
    },
    {
      path: "src/contexts/AuthContext.tsx",
      content: `import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase, isPreviewMode } from "../lib/supabase";

interface User { id: string; email: string; }

// Mock user for preview mode — allows auth-gated apps to render in sandbox
const PREVIEW_USER: User = {
  id: "preview-user",
  email: "preview@dayone.app"
};

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signUp: (email: string, password: string) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  signIn: async () => ({ error: "Not initialized" }),
  signUp: async () => ({ error: "Not initialized" }),
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // In preview mode, start with mock user and loading=false so auth-gated apps render
  const [user, setUser] = useState<User | null>(isPreviewMode ? PREVIEW_USER : null);
  const [loading, setLoading] = useState(!isPreviewMode);

  useEffect(() => {
    // Skip real auth flow in preview mode — mock user already set
    if (isPreviewMode) return;

    supabase.auth.getSession().then(({ data }: any) => {
      const u = data?.session?.user;
      setUser(u ? { id: u.id, email: u.email ?? "" } : null);
      setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event: any, session: any) => {
      const u = session?.user;
      setUser(u ? { id: u.id, email: u.email ?? "" } : null);
    });
    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return error ? { error: error.message } : {};
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({ email, password });
    return error ? { error: error.message } : {};
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
`,
    },

    // --- Database helper (Supabase only — NO localStorage) ---
    {
      path: "src/lib/database.ts",
      content: `import { supabase, isPreviewMode } from "./supabase";

// Generic data service — uses Supabase for ALL data persistence.
// In preview mode (no Supabase connection), returns empty data.
// NEVER uses localStorage for application data.

function uuid() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

interface ListOptions {
  column?: string;
  value?: string | number | boolean;
  orderBy?: string;
  ascending?: boolean;
  limit?: number;
}

export const db = {
  async list<T extends Record<string, any>>(table: string, opts?: ListOptions): Promise<T[]> {
    if (isPreviewMode) {
      console.log("[db.list] Preview mode — connect Supabase to persist data");
      return [];
    }
    let q = supabase.from(table).select("*");
    if (opts?.column && opts.value !== undefined) q = q.eq(opts.column, opts.value);
    if (opts?.orderBy) q = q.order(opts.orderBy, { ascending: opts.ascending !== false });
    if (opts?.limit) q = q.limit(opts.limit);
    const { data, error } = await q;
    if (error) throw new Error(error.message);
    return (data ?? []) as T[];
  },

  async get<T extends Record<string, any>>(table: string, id: string): Promise<T | null> {
    if (isPreviewMode) {
      console.log("[db.get] Preview mode — connect Supabase to persist data");
      return null;
    }
    const { data, error } = await supabase.from(table).select("*").eq("id", id).single();
    if (error) throw new Error(error.message);
    return data as T;
  },

  async create<T extends Record<string, any>>(table: string, record: Partial<T>): Promise<T> {
    const now = new Date().toISOString();
    const row = { id: uuid(), created_at: now, ...record } as T;
    if (isPreviewMode) {
      console.log("[db.create] Preview mode — connect Supabase to persist data");
      return row;
    }
    const { data, error } = await supabase.from(table).insert(row).select().single();
    if (error) throw new Error(error.message);
    return data as T;
  },

  async update<T extends Record<string, any>>(table: string, id: string, changes: Partial<T>): Promise<T> {
    const updated = { ...changes, updated_at: new Date().toISOString() } as Partial<T>;
    if (isPreviewMode) {
      console.log("[db.update] Preview mode — connect Supabase to persist data");
      return { id, ...updated } as T;
    }
    const { data, error } = await supabase.from(table).update(updated).eq("id", id).select().single();
    if (error) throw new Error(error.message);
    return data as T;
  },

  async remove(table: string, id: string): Promise<void> {
    if (isPreviewMode) {
      console.log("[db.remove] Preview mode — connect Supabase to persist data");
      return;
    }
    const { error } = await supabase.from(table).delete().eq("id", id);
    if (error) throw new Error(error.message);
  },

  async listMine<T extends Record<string, any>>(table: string, userId: string): Promise<T[]> {
    return db.list<T>(table, { column: "user_id", value: userId });
  },
};
`,
    },

    // --- Storage helper (Supabase Storage for file uploads) ---
    {
      path: "src/lib/storage.ts",
      content: `import { supabase, isPreviewMode } from "./supabase";

// Storage service — uses Supabase Storage for file uploads.
// Default bucket: "uploads" (created automatically when DayOne DB is provisioned)
// In preview mode (no Supabase connection), returns mock data.

const DEFAULT_BUCKET = "uploads";

interface UploadResult {
  path: string;
  url: string;
}

export const storage = {
  /**
   * Upload a file to Supabase Storage
   * @param file - File or Blob to upload
   * @param path - Optional path within the bucket (defaults to unique filename)
   * @param bucket - Bucket name (defaults to "uploads")
   */
  async upload(file: File | Blob, path?: string, bucket = DEFAULT_BUCKET): Promise<UploadResult> {
    const fileName = path || \`\${Date.now()}-\${(file as File).name || "file"}\`;

    if (isPreviewMode) {
      console.log("[storage.upload] Preview mode — connect DayOne DB to persist files");
      // Return a fake URL for preview
      const fakeUrl = URL.createObjectURL(file);
      return { path: fileName, url: fakeUrl };
    }

    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(fileName, file, { upsert: true });

    if (error) throw new Error(error.message);

    const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(data.path);
    return { path: data.path, url: urlData.publicUrl };
  },

  /**
   * Get a public URL for a file
   * @param path - Path to the file in the bucket
   * @param bucket - Bucket name (defaults to "uploads")
   */
  getUrl(path: string, bucket = DEFAULT_BUCKET): string {
    if (isPreviewMode) {
      console.log("[storage.getUrl] Preview mode — connect DayOne DB to get file URLs");
      return "";
    }
    const { data } = supabase.storage.from(bucket).getPublicUrl(path);
    return data.publicUrl;
  },

  /**
   * Download a file from storage
   * @param path - Path to the file in the bucket
   * @param bucket - Bucket name (defaults to "uploads")
   */
  async download(path: string, bucket = DEFAULT_BUCKET): Promise<Blob | null> {
    if (isPreviewMode) {
      console.log("[storage.download] Preview mode — connect DayOne DB to download files");
      return null;
    }
    const { data, error } = await supabase.storage.from(bucket).download(path);
    if (error) throw new Error(error.message);
    return data;
  },

  /**
   * Delete a file from storage
   * @param path - Path to the file in the bucket
   * @param bucket - Bucket name (defaults to "uploads")
   */
  async remove(path: string, bucket = DEFAULT_BUCKET): Promise<void> {
    if (isPreviewMode) {
      console.log("[storage.remove] Preview mode — connect DayOne DB to delete files");
      return;
    }
    const { error } = await supabase.storage.from(bucket).remove([path]);
    if (error) throw new Error(error.message);
  },

  /**
   * List files in a folder
   * @param folder - Folder path (defaults to root)
   * @param bucket - Bucket name (defaults to "uploads")
   */
  async list(folder = "", bucket = DEFAULT_BUCKET): Promise<string[]> {
    if (isPreviewMode) {
      console.log("[storage.list] Preview mode — connect DayOne DB to list files");
      return [];
    }
    const { data, error } = await supabase.storage.from(bucket).list(folder);
    if (error) throw new Error(error.message);
    return (data || []).map((f) => f.name);
  },
};
`,
    },

    // --- shadcn-lite UI Components (dark theme, no Radix, Tailwind only) ---
    {
      path: "src/components/ui/button.tsx",
      content: `import React from "react";
import { cn } from "../../lib/utils";

const variantStyles: Record<string, string> = {
  default: "bg-[rgb(var(--primary))] text-[rgb(var(--primary-foreground))] hover:bg-[rgb(var(--primary))]/90 shadow-sm",
  destructive: "bg-[rgb(var(--destructive))] text-white hover:bg-[rgb(var(--destructive))]/90 shadow-sm",
  outline: "border border-[rgb(var(--border))] bg-transparent text-slate-300 hover:bg-slate-800 hover:text-white",
  secondary: "bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white",
  ghost: "text-slate-400 hover:bg-slate-800 hover:text-white",
  link: "text-[rgb(var(--primary))] underline-offset-4 hover:underline",
};

const sizeStyles: Record<string, string> = {
  default: "h-10 px-4 py-2 text-sm",
  sm: "h-9 px-3 text-xs",
  lg: "h-11 px-8 text-base",
  icon: "h-10 w-10",
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  size?: "default" | "sm" | "lg" | "icon";
  loading?: boolean;
}

export function Button({ className, variant = "default", size = "default", loading, disabled, children, ...props }: ButtonProps) {
  return (
    <button
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--primary))]/50",
        "disabled:opacity-50 disabled:pointer-events-none",
        variantStyles[variant],
        sizeStyles[size],
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      )}
      {children}
    </button>
  );
}
`,
    },
    {
      path: "src/components/ui/card.tsx",
      content: `import React from "react";
import { cn } from "../../lib/utils";

export function Card({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("rounded-xl border border-slate-700/50 bg-slate-800/50 backdrop-blur shadow-sm", className)} {...props} />;
}

export function CardHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex flex-col space-y-1.5 p-6", className)} {...props} />;
}

export function CardTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cn("text-lg font-semibold text-white leading-none tracking-tight", className)} {...props} />;
}

export function CardDescription({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn("text-sm text-slate-400", className)} {...props} />;
}

export function CardContent({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("p-6 pt-0", className)} {...props} />;
}

export function CardFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex items-center p-6 pt-0", className)} {...props} />;
}
`,
    },
    {
      path: "src/components/ui/input.tsx",
      content: `import React from "react";
import { cn } from "../../lib/utils";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, id, ...props }, ref) => {
    const inputId = id || "input-" + Math.random().toString(36).slice(2, 9);
    return (
      <div className="w-full">
        {label && <label htmlFor={inputId} className="block text-sm font-medium text-slate-300 mb-1.5">{label}</label>}
        <input
          ref={ref}
          id={inputId}
          className={cn(
            "flex h-10 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white",
            "placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
            "disabled:cursor-not-allowed disabled:opacity-50",
            error && "border-rose-500 focus:ring-rose-500",
            className
          )}
          {...props}
        />
        {error && <p className="mt-1.5 text-sm text-rose-400">{error}</p>}
      </div>
    );
  }
);
Input.displayName = "Input";
`,
    },
    {
      path: "src/components/ui/textarea.tsx",
      content: `import React from "react";
import { cn } from "../../lib/utils";

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, label, error, id, ...props }, ref) => {
    const textareaId = id || "textarea-" + Math.random().toString(36).slice(2, 9);
    return (
      <div className="w-full">
        {label && <label htmlFor={textareaId} className="block text-sm font-medium text-slate-300 mb-1.5">{label}</label>}
        <textarea
          ref={ref}
          id={textareaId}
          className={cn(
            "flex min-h-[80px] w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white",
            "placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
            "disabled:cursor-not-allowed disabled:opacity-50",
            error && "border-rose-500 focus:ring-rose-500",
            className
          )}
          {...props}
        />
        {error && <p className="mt-1.5 text-sm text-rose-400">{error}</p>}
      </div>
    );
  }
);
Textarea.displayName = "Textarea";
`,
    },
    {
      path: "src/components/ui/badge.tsx",
      content: `import React from "react";
import { cn } from "../../lib/utils";

const variantStyles: Record<string, string> = {
  default: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  secondary: "bg-slate-700 text-slate-300 border-slate-600",
  success: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  warning: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  destructive: "bg-rose-500/10 text-rose-400 border-rose-500/20",
  outline: "bg-transparent text-slate-300 border-slate-600",
};

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: "default" | "secondary" | "success" | "warning" | "destructive" | "outline";
}

export function Badge({ className, variant = "default", ...props }: BadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors",
        variantStyles[variant],
        className
      )}
      {...props}
    />
  );
}
`,
    },
    {
      path: "src/components/ui/table.tsx",
      content: `import React from "react";
import { cn } from "../../lib/utils";

export function Table({ className, ...props }: React.HTMLAttributes<HTMLTableElement>) {
  return (
    <div className="relative w-full overflow-auto rounded-xl border border-slate-700/50 bg-slate-800/50">
      <table className={cn("w-full caption-bottom text-sm", className)} {...props} />
    </div>
  );
}

export function TableHeader({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) {
  return <thead className={cn("border-b border-slate-700/50 bg-slate-800/80", className)} {...props} />;
}

export function TableBody({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={cn("[&_tr:last-child]:border-0", className)} {...props} />;
}

export function TableFooter({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) {
  return <tfoot className={cn("border-t border-slate-700/50 bg-slate-800/50 font-medium text-white", className)} {...props} />;
}

export function TableRow({ className, ...props }: React.HTMLAttributes<HTMLTableRowElement>) {
  return <tr className={cn("border-b border-slate-700/30 transition-colors hover:bg-slate-700/20", className)} {...props} />;
}

export function TableHead({ className, ...props }: React.ThHTMLAttributes<HTMLTableCellElement>) {
  return <th className={cn("h-12 px-6 text-left align-middle font-medium text-slate-400 text-xs uppercase tracking-wider", className)} {...props} />;
}

export function TableCell({ className, ...props }: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return <td className={cn("px-6 py-4 align-middle text-sm text-slate-300", className)} {...props} />;
}

export function TableCaption({ className, ...props }: React.HTMLAttributes<HTMLTableCaptionElement>) {
  return <caption className={cn("mt-4 text-sm text-slate-500", className)} {...props} />;
}
`,
    },
    {
      path: "src/components/ui/select.tsx",
      content: `import React from "react";
import { cn } from "../../lib/utils";

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, label, error, options, placeholder, id, ...props }, ref) => {
    const selectId = id || "select-" + Math.random().toString(36).slice(2, 9);
    return (
      <div className="w-full">
        {label && <label htmlFor={selectId} className="block text-sm font-medium text-slate-300 mb-1.5">{label}</label>}
        <select
          ref={ref}
          id={selectId}
          className={cn(
            "flex h-10 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white",
            "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
            "disabled:cursor-not-allowed disabled:opacity-50",
            error && "border-rose-500",
            className
          )}
          {...props}
        >
          {placeholder && <option value="" disabled>{placeholder}</option>}
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {error && <p className="mt-1.5 text-sm text-rose-400">{error}</p>}
      </div>
    );
  }
);
Select.displayName = "Select";
`,
    },
    {
      path: "src/components/ui/dialog.tsx",
      content: `import React from "react";
import { cn } from "../../lib/utils";

interface DialogContextValue { open: boolean; onOpenChange: (open: boolean) => void; }
const DialogContext = React.createContext<DialogContextValue | null>(null);

export function Dialog({ open, onOpenChange, children }: { open: boolean; onOpenChange: (open: boolean) => void; children: React.ReactNode }) {
  return <DialogContext.Provider value={{ open, onOpenChange }}>{children}</DialogContext.Provider>;
}

export function DialogTrigger({ children, className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const ctx = React.useContext(DialogContext);
  return <button onClick={() => ctx?.onOpenChange(true)} className={className} {...props}>{children}</button>;
}

export function DialogContent({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const ctx = React.useContext(DialogContext);

  React.useEffect(() => {
    if (!ctx?.open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") ctx.onOpenChange(false); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [ctx?.open]);

  if (!ctx?.open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => ctx.onOpenChange(false)} />
      <div className={cn("relative z-50 w-full max-w-lg rounded-xl border border-slate-700/50 bg-slate-900 p-6 shadow-xl", className)} {...props}>
        <button
          onClick={() => ctx.onOpenChange(false)}
          className="absolute right-4 top-4 rounded-sm text-slate-400 hover:text-white transition-colors"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
        {children}
      </div>
    </div>
  );
}

export function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex flex-col space-y-1.5 mb-4", className)} {...props} />;
}

export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex justify-end gap-3 mt-6", className)} {...props} />;
}

export function DialogTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <h2 className={cn("text-lg font-semibold text-white leading-none tracking-tight", className)} {...props} />;
}

export function DialogDescription({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn("text-sm text-slate-400", className)} {...props} />;
}
`,
    },
    {
      path: "src/components/ui/tabs.tsx",
      content: `import React from "react";
import { cn } from "../../lib/utils";

const TabsContext = React.createContext<{ activeTab: string; setActiveTab: (id: string) => void } | null>(null);

export function Tabs({ defaultValue, children, className }: { defaultValue: string; children: React.ReactNode; className?: string }) {
  const [activeTab, setActiveTab] = React.useState(defaultValue);
  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab }}>
      <div className={cn("w-full", className)}>{children}</div>
    </TabsContext.Provider>
  );
}

export function TabsList({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={cn("inline-flex h-10 items-center justify-center rounded-lg bg-slate-800 p-1 text-slate-400", className)}>{children}</div>;
}

export function TabsTrigger({ value, children, className }: { value: string; children: React.ReactNode; className?: string }) {
  const ctx = React.useContext(TabsContext);
  if (!ctx) throw new Error("TabsTrigger must be within Tabs");
  const isActive = ctx.activeTab === value;
  return (
    <button
      onClick={() => ctx.setActiveTab(value)}
      className={cn(
        "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all",
        isActive ? "bg-slate-700 text-white shadow-sm" : "hover:bg-slate-700/50 hover:text-slate-300",
        className
      )}
    >
      {children}
    </button>
  );
}

export function TabsContent({ value, children, className }: { value: string; children: React.ReactNode; className?: string }) {
  const ctx = React.useContext(TabsContext);
  if (!ctx) throw new Error("TabsContent must be within Tabs");
  if (ctx.activeTab !== value) return null;
  return <div className={cn("mt-2", className)}>{children}</div>;
}
`,
    },
    {
      path: "src/components/ui/alert.tsx",
      content: `import React from "react";
import { cn } from "../../lib/utils";

const variantStyles: Record<string, string> = {
  default: "bg-slate-800 border-slate-700 text-slate-300",
  info: "bg-blue-500/10 border-blue-500/20 text-blue-300",
  success: "bg-emerald-500/10 border-emerald-500/20 text-emerald-300",
  warning: "bg-amber-500/10 border-amber-500/20 text-amber-300",
  destructive: "bg-rose-500/10 border-rose-500/20 text-rose-300",
};

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "default" | "info" | "success" | "warning" | "destructive";
}

export function Alert({ className, variant = "default", ...props }: AlertProps) {
  return <div role="alert" className={cn("relative w-full rounded-lg border p-4", variantStyles[variant], className)} {...props} />;
}

export function AlertTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <h5 className={cn("mb-1 font-medium leading-none tracking-tight", className)} {...props} />;
}

export function AlertDescription({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <div className={cn("text-sm opacity-90", className)} {...props} />;
}
`,
    },
    {
      path: "src/components/ui/avatar.tsx",
      content: `import React from "react";
import { cn } from "../../lib/utils";

export function Avatar({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full", className)} {...props} />;
}

export function AvatarImage({ src, alt, className }: { src: string; alt?: string; className?: string }) {
  return <img src={src} alt={alt || ""} className={cn("aspect-square h-full w-full object-cover", className)} />;
}

export function AvatarFallback({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex h-full w-full items-center justify-center rounded-full bg-slate-700 text-sm font-medium text-slate-300", className)} {...props} />;
}
`,
    },
    {
      path: "src/components/ui/separator.tsx",
      content: `import React from "react";
import { cn } from "../../lib/utils";

export function Separator({ className, orientation = "horizontal", ...props }: React.HTMLAttributes<HTMLDivElement> & { orientation?: "horizontal" | "vertical" }) {
  return (
    <div
      role="separator"
      className={cn(
        "shrink-0 bg-slate-700/50",
        orientation === "horizontal" ? "h-[1px] w-full" : "h-full w-[1px]",
        className
      )}
      {...props}
    />
  );
}
`,
    },
    {
      path: "src/components/ui/index.ts",
      content: `// shadcn-lite UI Component Library
// Dark-theme, Tailwind-only components — no external dependencies.
// Import individually: import { Button } from "./button"
// Or from barrel: import { Button, Card, Input } from "../ui"

export { Button, type ButtonProps } from "./button";
export { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "./card";
export { Input, type InputProps } from "./input";
export { Textarea, type TextareaProps } from "./textarea";
export { Badge, type BadgeProps } from "./badge";
export { Table, TableHeader, TableBody, TableFooter, TableRow, TableHead, TableCell, TableCaption } from "./table";
export { Select, type SelectProps } from "./select";
export { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from "./dialog";
export { Tabs, TabsList, TabsTrigger, TabsContent } from "./tabs";
export { Alert, AlertTitle, AlertDescription, type AlertProps } from "./alert";
export { Avatar, AvatarImage, AvatarFallback } from "./avatar";
export { Separator } from "./separator";
`,
    },
  ];
}
